/**
 * workosAuth.ts — WorkOS sign-in for the SPA (server-side confidential flow).
 *
 * The OAuth code exchange runs entirely on the API as a confidential client; the
 * browser never talks to api.workos.com. The session is anchored by a first-party
 * HttpOnly refresh cookie that GET /v1/auth/workos/callback sets, so this module
 * only ever needs four moves:
 *
 *   initAuth()  → POST /v1/auth/workos/refresh to mint the first access token
 *   signIn()    → full-page redirect to GET /v1/auth/workos/login
 *   signOut()   → clear the local token + POST /v1/auth/workos/logout
 *   (refresher) → POST /v1/auth/workos/refresh again to recover a 401 (api/client)
 *
 * Auth state lives in a tiny module-level store so the React AuthProvider can
 * subscribe without owning the lifecycle.
 *
 * Active only when VITE_AUTH_MODE=workos. In stub/jwt builds this module is still
 * imported (App.tsx pulls in signOut) but inert: initAuth() is never called and
 * the api client only invokes the refresher when isWorkOSAuth() is true.
 */

import {
  setToken,
  clearToken,
  registerWorkosRefresher,
  registerAuthFailureHandler,
} from '../api/client'
import { stashDealReturn, restoreDealReturn } from '../components/dashboard/pages/sales/dealLink'

// Same-origin in production (nginx proxies /v1 → the API). credentials:'include'
// carries the first-party refresh cookie (Path=/v1/auth/workos) on refresh/logout.
const LOGIN_URL = '/v1/auth/workos/login'
const REFRESH_URL = '/v1/auth/workos/refresh'
const LOGOUT_URL = '/v1/auth/workos/logout'

/**
 * Minimal local stand-in for the WorkOS user shape (replaces the authkit-js type).
 * The server-side flow returns only an access token — no profile — so `user` is
 * currently always null; the field is retained so the store shape and the
 * AuthProvider interface are unchanged. Decode the access token here later if the
 * UI needs profile fields.
 */
export interface User {
  id: string
  email?: string
}

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated'
export interface AuthSnapshot {
  status: AuthStatus
  user: User | null
}

// ── Module-level store (subscribed to by AuthProvider) ──────────────────────────

let snapshot: AuthSnapshot = { status: 'loading', user: null }
const listeners = new Set<() => void>()

function set(next: Partial<AuthSnapshot>): void {
  snapshot = { ...snapshot, ...next }
  listeners.forEach((notify) => notify())
}

export function subscribeAuth(onChange: () => void): () => void {
  listeners.add(onChange)
  return () => {
    listeners.delete(onChange)
  }
}

export function getAuthSnapshot(): AuthSnapshot {
  return snapshot
}

// ── Refresh exchange ────────────────────────────────────────────────────────────

let refreshInFlight: Promise<string> | null = null

/**
 * Trade the HttpOnly refresh cookie for a fresh access token, persist it for the
 * api client, and return it.
 *
 * Single-flight: WorkOS refresh tokens are single-use and rotate on every call,
 * so concurrent callers (initAuth + several 401 recoveries firing at once) MUST
 * share one POST — otherwise the losers replay an already-spent cookie, get a
 * 401, and tear the session down. Coalesce to one in-flight request.
 */
function refreshAccessToken(): Promise<string> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

async function doRefresh(): Promise<string> {
  const res = await fetch(REFRESH_URL, { method: 'POST', credentials: 'include' })
  if (!res.ok) throw new Error(`workos refresh failed: ${res.status}`)
  const data = (await res.json()) as { access_token?: string }
  if (!data.access_token) throw new Error('workos refresh: response missing access_token')
  setToken(data.access_token)
  return data.access_token
}

// ── Public API (consumed by AuthProvider) ──────────────────────────────────────

/** Restore the session on app start; a single refresh resolves the initial status. */
export async function initAuth(): Promise<void> {
  // Teach the (otherwise WorkOS-agnostic) api client how to recover a 401 and how
  // to bail out. Idempotent — safe under StrictMode's dev double-mount.
  registerWorkosRefresher(refreshAccessToken)
  registerAuthFailureHandler(() => {
    void signOut()
  })

  try {
    await refreshAccessToken()
    // A deal link opened signed out comes back from the callback as "/" — put
    // its address back before the app mounts and reads it (slice 9).
    restoreDealReturn()
    set({ status: 'authenticated', user: null })
  } catch {
    set({ status: 'unauthenticated', user: null })
  }
}

/** Redirect to the server-side login endpoint (302 → WorkOS hosted AuthKit). */
export function signIn(): void {
  stashDealReturn(window.location.pathname)
  window.location.assign(LOGIN_URL)
}

/** Clear the local token and the server-side refresh cookie, then go unauthenticated. */
export async function signOut(): Promise<void> {
  clearToken()
  try {
    await fetch(LOGOUT_URL, { method: 'POST', credentials: 'include' })
  } catch {
    /* network error — the local token is already gone; treat as signed out */
  }
  set({ status: 'unauthenticated', user: null })
}
